// components/plScenario/table/SearchModal.jsx
import React from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

const SearchModal = ({
  isOpen,
  searchTerm,
  searchResults,
  currentSearchIndex,
  onSearchChange,
  onClose,
  onNext,
  onPrev,
  onSelectResult,
  onClearAfterDelay,
  onNavigateToResult, // NEW: direct navigation function
}) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  onClose();
                } else if (e.key === 'Enter' && searchResults.length > 0) {
                  onNavigateToResult(currentSearchIndex); // Navigate to current result
                  onClearAfterDelay();
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  onNext();
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  onPrev();
                }
              }}
              placeholder="Search accounts... (Enter to navigate, Esc to close, ↑↓ to navigate)"
              autoFocus
              className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={onClose}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          {searchTerm && (
            <div className="mt-3 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {searchResults.length > 0 ? (
                  <>
                    Found <span className="font-semibold">{searchResults.length}</span> result{searchResults.length !== 1 ? 's' : ''}
                    {currentSearchIndex >= 0 && (
                      <span className="ml-2 text-blue-600 font-semibold">
                        (Viewing {currentSearchIndex + 1}/{searchResults.length})
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-red-600">No results found</span>
                )}
              </div>
              
              {searchResults.length > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={onPrev}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={onNext}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {searchTerm && searchResults.length > 0 && (
          <div className="max-h-64 overflow-y-auto border-t border-gray-200">
            <div className="p-2">
              <div className="text-xs font-semibold text-gray-500 px-3 py-2">
                RESULTS
              </div>
              {searchResults.map((result, index) => {
                const fqn = result.fully_qualified_name || result.account_name;
                const parts = fqn.split(':');
                const displayName = parts.length > 1 ? parts[parts.length - 1] : result.account_name;
                const category = parts.length > 1 ? parts[0] : 'Other';
                
                return (
                  <button
                    key={result.account_id}
                    onClick={() => {
                      onSelectResult(index); // Set the index
                      onNavigateToResult(index); // Navigate immediately
                      onClearAfterDelay(); // Clear after delay
                    }}
                    className={`w-full text-left px-3 py-2 rounded hover:bg-gray-100 transition-colors ${
                      index === currentSearchIndex ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{displayName}</div>
                        <div className="text-xs text-gray-500">{category} • {result.account_type}</div>
                      </div>
                      {index === currentSearchIndex && (
                        <div className="ml-2">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            Current
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">↑</kbd>
              <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">↓</kbd>
              <span>Navigate</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">Enter</kbd>
              <span>Go to Result</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">Esc</kbd>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;